import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { apiGet, apiPost } from "../utils/apiAuth";
import { getTopicsSubtopicsBySubject } from "../utils/topicsSubtopicsCache";
import { Phase, Subject, Topic, Subtopic, ContentItem, Subtask } from "./ProjectConfigurationTypes.ts";
import { SubtaskTypes } from "./projectConfigConstants.ts";
import PhaseComponent from "./PhaseComponent.tsx";
import { Modal, Button, Spinner } from "react-bootstrap";
import ProjectConfigurationHeader from "./ProjectConfigurationHeader.tsx";

const ProjectConfiguration: React.FC = () => {
  const location = useLocation();
  const navigationState = (location.state as any) || {};
  const [phases, setPhases] = useState<Phase[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [projectAllowedSubjects, setProjectAllowedSubjects] = useState<string[]>([]);
  const [topicsCache, setTopicsCache] = useState<Record<string, Topic[]>>({});
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  // Load filter state from sessionStorage on mount (not from navigation state)
  // Navigation state should not auto-populate filters - user must apply filters manually
  // Only load if there's a saved track (which means filters were previously applied)
  const loadFilterState = () => {
    try {
      const savedTrack = sessionStorage.getItem('projectConfig_selectedTrack');
      // Only restore filters if track exists (meaning user previously applied filters)
      // Don't restore if only project exists without track (could be from navigation)
      if (savedTrack) {
        const savedProject = sessionStorage.getItem('projectConfig_selectedProject');
        const savedProjectName = sessionStorage.getItem('projectConfig_selectedProjectName');
        return {
          track: JSON.parse(savedTrack),
          project: savedProject ? JSON.parse(savedProject) : "",
          projectName: savedProjectName ? JSON.parse(savedProjectName) : "",
          wasApplied: true // Mark that filters were previously applied
        };
      }
      return {
        track: "",
        project: "",
        projectName: "",
        wasApplied: false
      };
    } catch (e) {
      return {
        track: "",
        project: "",
        projectName: "",
        wasApplied: false
      };
    }
  };

  const initialFilterState = loadFilterState();
  const [selectedTrack, setSelectedTrack] = useState<string>(initialFilterState.track);
  const [selectedProject, setSelectedProject] = useState<string>(initialFilterState.project);
  const [selectedProjectName, setSelectedProjectName] = useState<string>(initialFilterState.projectName);
  // Track if project was selected via filter (not from navigation state)
  // Only true if filters were previously applied (track exists in sessionStorage)
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(initialFilterState.wasApplied);
  const [loading, setLoading] = useState<boolean>(false);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewContent, setPreviewContent] = useState<{ type: "video" | "notes"; content: any; subtask: any } | null>(null);
  const [contentStatus, setContentStatus] = useState<{
    video?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    notes?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    mcq?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
    project_coding?: { assigned?: string[]; uploaded?: string[]; approved?: string[] };
  } | null>(null);

  /**
   * Fetches content status for the project
   * @param projectId - The project ID to fetch status for
   */
  const fetchContentStatus = async (projectId: string) => {
    if (!projectId) {
      setContentStatus(null);
      return;
    }
    
    try {
      const response = await apiGet(`/api/admin/content/project/content_status/${projectId}/`);
      if (response.data) {
        setContentStatus({
          video: response.data.video || {},
          notes: response.data.notes || {},
          mcq: response.data.mcq || {},
          coding: response.data.coding || {},
          project_coding: response.data.project_coding || {}
        });
      }
    } catch (error) {
      setContentStatus(null);
    }
  };

  /**
   * Effect: Save filter state to sessionStorage when it changes
   */
  useEffect(() => {
    try {
      if (selectedTrack) {
        sessionStorage.setItem('projectConfig_selectedTrack', JSON.stringify(selectedTrack));
      } else {
        sessionStorage.removeItem('projectConfig_selectedTrack');
      }
      if (selectedProject) {
        sessionStorage.setItem('projectConfig_selectedProject', JSON.stringify(selectedProject));
        sessionStorage.setItem('projectConfig_selectedProjectName', JSON.stringify(selectedProjectName));
      } else {
        sessionStorage.removeItem('projectConfig_selectedProject');
        sessionStorage.removeItem('projectConfig_selectedProjectName');
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, [selectedTrack, selectedProject, selectedProjectName]);

  /**
   * Effect: Fetch content status when selectedProject changes
   * Only fetch if filter is actually applied (not from navigation state)
   */
  useEffect(() => {
    if (selectedProject && isFilterApplied) {
      fetchContentStatus(selectedProject);
    } else {
      setContentStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, isFilterApplied]);

  /**
   * Effect: Auto-save project configuration when phases change
   * Debounced to avoid excessive API calls (2 second delay)
   * Only saves if project is selected and phases exist
   */
  // Auto-save removed as per request

  /**
   * Effect: Save expandedItems to localStorage when it changes
   */
  useEffect(() => {
    if (selectedProject && expandedItems.size > 0) {
      try {
        const expandedArray = Array.from(expandedItems);
        localStorage.setItem(`projectConfig_expandedItems_${selectedProject}`, JSON.stringify(expandedArray));
      } catch (e) {
        // Ignore storage errors
      }
    }
  }, [expandedItems, selectedProject]);

  /**
   * Effect: Restore expandedItems from localStorage on mount or when project changes
   */
  useEffect(() => {
    if (selectedProject && phases.length > 0) {
      try {
        const savedExpanded = localStorage.getItem(`projectConfig_expandedItems_${selectedProject}`);
        if (savedExpanded) {
          const expandedArray = JSON.parse(savedExpanded);
          if (Array.isArray(expandedArray) && expandedArray.length > 0) {
            setExpandedItems(new Set(expandedArray));
            return; // Exit early if we restored saved state
          }
        }
      } catch (e) {
        // Ignore storage errors
      }
    }
    
    // Auto-expand first items only if no saved state exists
    if (phases.length > 0 && expandedItems.size === 0) {
      const firstPhase = phases[0];
      if (firstPhase) {
        const newSet = new Set<string>();
        // Step 2: Add first phase
        newSet.add(firstPhase.id);
        
        // Step 3: Add first part
        if (firstPhase.parts.length > 0) {
          const firstPart = firstPhase.parts[0];
          const firstPartId = `${firstPhase.id}-${firstPart.id}`;
          newSet.add(firstPartId);
          
          // Step 4: Add first task
          if (firstPart.tasks.length > 0) {
            const firstTask = firstPart.tasks[0];
            const firstTaskId = `${firstPartId}-${firstTask.id}`;
            newSet.add(firstTaskId);
            
            // Step 5: Add first subtask
            if (firstTask.subtasks.length > 0) {
              const firstSubtask = firstTask.subtasks[0];
              const firstSubtaskId = `${firstTaskId}-${firstSubtask.id}`;
              newSet.add(firstSubtaskId);
            }
          }
        }
        
        // Step 6: Update state
        setExpandedItems(newSet);
      }
    }
  }, [phases.length, selectedProject]);

  /**
   * Updates the subjects state when subjects change
   * @param newSubjects - Array of subject objects to set
   */
  const handleSubjectsChange = (newSubjects: Subject[]) => {
    setSubjects(newSubjects);
  };

  /**
   * Handles project selection change and transforms project data into phases structure
   * Step 1: Update selected project state (projectId, projectName, allowedSubjects)
   * Step 2: Check if projectData contains content array
   * Step 3: Transform phases data (map phase_id/phase_name to id/name)
   * Step 4: Transform parts data (map part_id/part_name to id/name)
   * Step 5: Transform tasks data (map task_id/task_name to id/name)
   * Step 6: Transform subtasks data (handle select/assign modes, determine selectedContentLevel)
   * Step 7: Collect unique subject IDs from all subtasks
   * Step 8: Fetch topics for each unique subject ID
   * Step 9: Set transformed phases in state
   * @param projectId - The selected project ID
   * @param projectName - The selected project name
   * @param allowedSubjects - Optional array of allowed subject IDs for this project
   * @param projectData - Optional project data object containing content structure
   */

  const handleProjectChange = async (projectId: string, projectName: string, allowedSubjects?: string[], projectData?: any) => {
    // Set loading state
    setLoading(true);
    
    try {
      // Step 1: Update project state
      setSelectedProject(projectId);
      setSelectedProjectName(projectName);
      setProjectAllowedSubjects(allowedSubjects || []);
      // Mark that filter has been applied (project selected via dropdown)
      setIsFilterApplied(!!projectId);
      
      // Only fetch content status and load phases if project is selected (filter is applied)
      if (projectId) {
        // Fetch content status for the project
        await fetchContentStatus(projectId);
      
      // Step 2: Check if project data exists
      if (projectData?.content && Array.isArray(projectData.content) && projectData.content.length > 0) {
      // Step 3-6: Transform project data structure
      const transformedPhases = projectData.content.map((phase: any) => ({
        id: capitalizeId(phase.phase_id || phase.id || ""),
        name: phase.phase_name || phase.name || "",
        parts: (phase.parts || []).map((part: any) => ({
          id: capitalizeId(part.part_id || part.id || ""),
          name: part.part_name || part.name || "",
          tasks: (part.tasks || []).map((task: any) => ({
            id: capitalizeId(task.task_id || task.id || ""),
            name: task.task_name || task.name || "",
            subtasks: (task.data || task.subtasks || []).map((subtask: any) => {
              const isVideoOrNotes = subtask.type === "video" || subtask.type === "notes";
              const isMcqCoding = subtask.type === "mcq" || subtask.type === "coding";
              const isAssignMode = subtask.mode === "assign";
              const isSelectMode = subtask.mode === "select";
              
              // Determine selectedContentLevel for mcq/coding
              let selectedContentLevel = "";
              let selectedContentId = "";
              
              if (isVideoOrNotes && (isSelectMode || isAssignMode)) {
                selectedContentLevel = subtask.level || "";
                selectedContentId = subtask.id || "";
              } else if (isMcqCoding) {
                // Auto-select level1 if it has data, otherwise check level2, level3
                if (Array.isArray(subtask.level1) && subtask.level1.length > 0) {
                  selectedContentLevel = "level1";
                  selectedContentId = subtask.level1.map((id: any) => typeof id === "string" ? id : id.toString()).join(",");
                } else if (Array.isArray(subtask.level2) && subtask.level2.length > 0) {
                  selectedContentLevel = "level2";
                  selectedContentId = subtask.level2.map((id: any) => typeof id === "string" ? id : id.toString()).join(",");
                } else if (Array.isArray(subtask.level3) && subtask.level3.length > 0) {
                  selectedContentLevel = "level3";
                  selectedContentId = subtask.level3.map((id: any) => typeof id === "string" ? id : id.toString()).join(",");
                } else {
                  selectedContentLevel = subtask.selectedContentLevel || "";
                  selectedContentId = subtask.selectedContentId || "";
                }
              } else {
                selectedContentLevel = subtask.selectedContentLevel || "";
                selectedContentId = subtask.selectedContentId || "";
              }
              
              const transformedSubtask: any = {
                id: capitalizeId(subtask.subtask_id || subtask.id || ""),
                name: subtask.subtask_name || subtask.name || "",
                isMandatory: subtask.is_mandatory ?? subtask.isMandatory ?? true,
                showDropdowns: !!(subtask.mode || subtask.actionType || subtask.subject_id || subtask.subjectId),
                actionType: subtask.mode || subtask.actionType,
                subjectId: subtask.subject_id || subtask.subjectId || "",
                topicId: subtask.topic_id || subtask.topicId || "",
                subtopicId: subtask.sub_topic_id || subtask.subtopicId || "",
                subtaskType: (subtask.type || subtask.subtaskType || "") as SubtaskTypes,
                level: subtask.level || "",
                selectedContentId: selectedContentId,
                selectedContentTime: isVideoOrNotes && isSelectMode ? subtask.time : (subtask.selectedContentTime || ""),
                selectedContentLevel: selectedContentLevel,
                createdContent: subtask.createdContent,
                availableContent: subtask.availableContent,
                // Preserve level arrays for mcq/coding types
                level1: subtask.level1 || [],
                level2: subtask.level2 || [],
                level3: subtask.level3 || []
              };
              return transformedSubtask as Subtask;
            })
          }))
        }))
      }));
      setPhases(transformedPhases);
      
      const subjectsToFetch = new Set<string>();
      transformedPhases.forEach((phase: Phase) => {
        phase.parts.forEach((part: any) => {
          part.tasks.forEach((task: any) => {
            task.subtasks.forEach((subtask: any) => {
              if (subtask.subjectId) {
                subjectsToFetch.add(subtask.subjectId);
              }
            });
          });
        });
      });
      
      subjectsToFetch.forEach((subjectId: string) => {
        fetchTopics(subjectId);
      });
    } else {
      setPhases([]);
    }
    } else {
      // If no project selected, clear phases and content status
      setPhases([]);
      setContentStatus(null);
    }
    } finally {
      // Always set loading to false when done
      setLoading(false);
    }
  };

  const filteredSubjects = projectAllowedSubjects.length > 0
    ? subjects.filter(subject => projectAllowedSubjects.includes(subject.subject_id))
    : subjects;

  /**
   * Fetches topics and subtopics for a given subject with caching
   * Step 1: Validate subjectId exists
   * Step 2: Check if topics are already in memory cache
   * Step 3: Check sessionStorage for cached topics
   * Step 4: If cached data exists, parse and use it
   * Step 5: If not cached, fetch from API
   * Step 6: Store fetched topics in both memory cache and sessionStorage
   * @param subjectId - The subject ID to fetch topics for
   */
  const fetchTopics = async (subjectId: string) => {
    // Step 1: Validation
    if (!subjectId) return;
    // Step 2: Check memory cache
    if (topicsCache[subjectId]) return;

    try {
      // Step 3/4/5: session first via shared helper, API only on miss
      const topics = await getTopicsSubtopicsBySubject(subjectId);
      setTopicsCache(prev => ({ ...prev, [subjectId]: topics }));
    } catch (error) {
    }
  };

  /**
   * Capitalizes the first letter of an ID if it's not already capitalized
   * @param id - The ID string to capitalize
   * @returns ID with first letter capitalized (e.g., "phase1" -> "Phase1")
   */
  const capitalizeId = (id: string): string => {
    if (!id || id.length === 0) return id;
    const firstChar = id.charAt(0);
    if (firstChar >= 'A' && firstChar <= 'Z') {
      return id; // Already capitalized
    }
    return firstChar.toUpperCase() + id.slice(1);
  };

  /**
   * Generates a sequential ID for phases, parts, tasks, or subtasks
   * @param prefix - The prefix for the ID (e.g., "phase", "part", "task", "subtask")
   * @param index - The zero-based index to convert to 1-based ID
   * @returns Formatted ID string with first letter capitalized (e.g., "Phase1", "Part2")
   */
  const generateId = (prefix: string, index: number) => {
    const capitalizedPrefix = prefix.charAt(0).toUpperCase() + prefix.slice(1);
    return `${capitalizedPrefix}${index + 1}`;
  };

  /**
   * Expands an item and ensures parent items are also expanded, collapsing siblings at the same level
   * Step 1: Parse the ID to determine hierarchy level (phase, part, task, or subtask)
   * Step 2: For phase (1 part): Collapse other phases, add this phase
   * Step 3: For part (2 parts): Ensure parent phase is expanded, collapse sibling parts, add this part
   * Step 4: For task (3 parts): Ensure parent phase and part are expanded, collapse sibling tasks, add this task
   * Step 5: For subtask (4 parts): Ensure all parents are expanded, collapse sibling subtasks, add this subtask
   * @param id - The hierarchical ID (e.g., "phase1", "phase1-part1", "phase1-part1-task1", "phase1-part1-task1-subtask1")
   */
  const expandItem = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      const parts = id.split('-');
      
      // Step 2: Handle phase level (1 part)
      if (parts.length === 1) {
        // Collapse other phases
        newSet.forEach(item => {
          if (item.split('-').length === 1) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      } 
      // Step 3: Handle part level (2 parts)
      else if (parts.length === 2) {
        const parentId = parts[0];
        // Ensure parent phase is expanded
        if (!newSet.has(parentId)) {
          newSet.add(parentId);
        }
        // Collapse sibling parts
        newSet.forEach(item => {
          const itemParts = item.split('-');
          if (itemParts.length === 2 && itemParts[0] === parentId) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      } 
      // Step 4: Handle task level (3 parts)
      else if (parts.length === 3) {
        const parentId = `${parts[0]}-${parts[1]}`;
        // Ensure parent phase and part are expanded
        if (!newSet.has(parts[0])) {
          newSet.add(parts[0]);
        }
        if (!newSet.has(parentId)) {
          newSet.add(parentId);
        }
        // Collapse sibling tasks
        newSet.forEach(item => {
          const itemParts = item.split('-');
          if (itemParts.length === 3 && `${itemParts[0]}-${itemParts[1]}` === parentId) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      } 
      // Step 5: Handle subtask level (4 parts)
      else if (parts.length === 4) {
        const parentId = `${parts[0]}-${parts[1]}-${parts[2]}`;
        // Ensure all parents are expanded
        if (!newSet.has(parts[0])) {
          newSet.add(parts[0]);
        }
        if (!newSet.has(`${parts[0]}-${parts[1]}`)) {
          newSet.add(`${parts[0]}-${parts[1]}`);
        }
        if (!newSet.has(parentId)) {
          newSet.add(parentId);
        }
        // Collapse sibling subtasks
        newSet.forEach(item => {
          const itemParts = item.split('-');
          if (itemParts.length === 4 && `${itemParts[0]}-${itemParts[1]}-${itemParts[2]}` === parentId) {
            newSet.delete(item);
          }
        });
        newSet.add(id);
      }
      
      return newSet;
    });
  };

  /**
   * Handles track selection - clears filter applied flag when track changes
   */
  const handleTrackChange = (trackId: string) => {
    setSelectedTrack(trackId);
    // Clear filter applied flag and project when track changes (user needs to apply filter again)
    if (!trackId) {
      setIsFilterApplied(false);
      setSelectedProject("");
      setSelectedProjectName("");
      setPhases([]);
      setContentStatus(null);
    } else {
      // When track changes, also clear project selection so user must select project again
      setIsFilterApplied(false);
      setSelectedProject("");
      setSelectedProjectName("");
      setPhases([]);
      setContentStatus(null);
    }
  };

  /**
   * Toggles the expand/collapse state of an item, collapsing siblings at the same level
   * Step 1: Check if item is currently expanded - if yes, collapse it
   * Step 2: If not expanded, parse ID to determine level
   * Step 3: Collapse siblings at the same level (phases, parts, tasks, or subtasks)
   * Step 4: Expand the selected item
   * @param id - The hierarchical ID of the item to toggle
   */
  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      
      // Step 1: Toggle if already expanded
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        // Step 2: Parse ID to determine level
        const parts = id.split('-');
        
        // Step 3 & 4: Handle different hierarchy levels
        if (parts.length === 1) {
          // Phase level: collapse other phases
          newSet.forEach(item => {
            if (item.split('-').length === 1) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        } else if (parts.length === 2) {
          // Part level: collapse sibling parts
          const parentId = parts[0];
          newSet.forEach(item => {
            const itemParts = item.split('-');
            if (itemParts.length === 2 && itemParts[0] === parentId) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        } else if (parts.length === 3) {
          // Task level: collapse sibling tasks
          const parentId = `${parts[0]}-${parts[1]}`;
          newSet.forEach(item => {
            const itemParts = item.split('-');
            if (itemParts.length === 3 && `${itemParts[0]}-${itemParts[1]}` === parentId) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        } else if (parts.length === 4) {
          // Subtask level: collapse sibling subtasks
          const parentId = `${parts[0]}-${parts[1]}-${parts[2]}`;
          newSet.forEach(item => {
            const itemParts = item.split('-');
            if (itemParts.length === 4 && `${itemParts[0]}-${itemParts[1]}-${itemParts[2]}` === parentId) {
              newSet.delete(item);
            }
          });
          newSet.add(id);
        }
      }
      
      return newSet;
    });
  };

  /**
   * Checks if an item is currently expanded
   * @param id - The hierarchical ID of the item to check
   * @returns true if the item is expanded, false otherwise
   */
  const isExpanded = (id: string) => expandedItems.has(id);

  /**
   * Opens the preview modal for video or notes content
   * @param phaseIndex - The phase index
   * @param partIndex - The part index
   * @param taskIndex - The task index
   * @param subtaskIndex - The subtask index
   * @param type - The content type: "video" or "notes"
   * @param content - The content object to preview
   */
  const handlePreviewContent = (phaseIndex: number, partIndex: number, taskIndex: number, subtaskIndex: number, type: "video" | "notes", content: any) => {
    const subtask = phases[phaseIndex].parts[partIndex].tasks[taskIndex].subtasks[subtaskIndex];
    setPreviewContent({ type, content, subtask });
    setShowPreviewModal(true);
  };

  /**
   * Builds the VdoCipher video player URL from OTP and playback info
   * @param otp - The OTP token for video authentication
   * @param playback_info - The playback info token
   * @returns The complete video player URL
   */
  const buildVideoUrl = (otp: string, playback_info: string): string => {
    return `https://player.vdocipher.com/v2/?otp=${otp}&playbackInfo=${playback_info}`;
  };

  
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <div className="border rounded-2 bg-white" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
        <ProjectConfigurationHeader
          selectedTrack={selectedTrack}
          selectedProject={selectedProject}
          selectedProjectName={selectedProjectName}
          loading={loading}
          isFilterApplied={isFilterApplied}
          onTrackChange={handleTrackChange}
          onProjectChange={handleProjectChange}
          onSubjectsChange={handleSubjectsChange}
        />
        <div className="p-3" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        {loading ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: "100%", minHeight: "400px" }}>
            <div className="text-center">
              <Spinner animation="border" role="status" className="mb-3" style={{ width: "3rem", height: "3rem" }}>
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <h5 className="text-muted">Loading project configuration...</h5>
            </div>
          </div>
        ) : !selectedProject || !isFilterApplied ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: "100%", minHeight: "400px" }}>
            <div className="text-center text-muted">
              <h5 className="mb-3">No Project Selected</h5>
              <p className="mb-0">Please select a Track and Project from the filters above to view the configuration.</p>
            </div>
          </div>
        ) : phases.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center" style={{ height: "100%", minHeight: "400px" }}>
            <div className="text-center text-muted">
              <h5 className="mb-3">No Configuration Data</h5>
              <p className="mb-0">This project has no configuration data to display.</p>
            </div>
          </div>
        ) : (
          <div>
            {phases.map((phase, phaseIndex) => {
              const phaseId = phase.id;
              return (
                <div key={phaseId}>
                  <PhaseComponent
                  phase={phase}
                  phaseIndex={phaseIndex}
                  phaseId={phaseId}
                  isExpanded={isExpanded(phaseId)}
                  projectId={selectedProject}
                  subjects={subjects}
                  topicsCache={topicsCache}
                  contentStatus={contentStatus}
                  dragHandleProps={undefined}
                  onToggleExpand={() => toggleExpand(phaseId)}
                  onPartToggleExpand={(partId) => toggleExpand(partId)}
                  onTaskToggleExpand={(taskId) => toggleExpand(taskId)}
                  onPreviewContent={(partIndex, taskIndex, subtaskIndex, type, content) => handlePreviewContent(phaseIndex, partIndex, taskIndex, subtaskIndex, type, content)}
                  onFetchTopics={fetchTopics}
                  isPartExpanded={(partId) => isExpanded(partId)}
                  isTaskExpanded={(taskId) => isExpanded(taskId)}
                  isSubtaskExpanded={(subtaskId) => isExpanded(subtaskId)}
                  toggleSubtaskExpand={(subtaskId) => toggleExpand(subtaskId)}
                />
                </div>
              );
            })}
          </div>
        )}
        </div>
        </div>

      {showPreviewModal && previewContent && (
        <Modal
          show={showPreviewModal}
          onHide={() => {
            setShowPreviewModal(false);
            setPreviewContent(null);
          }}
          centered
          size="lg"
        >
          <Modal.Header closeButton>
            <Modal.Title>Preview</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {previewContent.type === "video" && previewContent.content.otp && previewContent.content.playback_info && 
            previewContent.content.otp.trim() !== "" && previewContent.content.playback_info.trim() !== "" ? (
              <iframe
                src={buildVideoUrl(previewContent.content.otp, previewContent.content.playback_info)}
                width="100%"
                height="500px"
                title="Video Preview"
                allowFullScreen
                allow="encrypted-media; autoplay; clipboard-write"
                style={{ border: "none" }}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            ) : previewContent.type === "notes" && previewContent.content.content && previewContent.content.content.trim() !== "" ? (
              <div className="p-0 m-0 ps-3" dangerouslySetInnerHTML={{ __html: previewContent.content.content }} />
            ) : (
              <div className="alert alert-warning">
                {previewContent.type === "video" ? "Video content not updated" : "Notes content not updated"}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => {
              setShowPreviewModal(false);
              setPreviewContent(null);
            }}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default ProjectConfiguration;

